/* ================================================================
 * Copyright © 2025 Tobeunicorn Co., Ltd. All rights reserved.
 *
 * 프로젝트명 : ArmyLink V1.0
 * 설명 : 홍보 결과 목록 > 관심표명자 등록 컴포넌트
 * 버전 : 1.0
 * 파일명 : PromoInterestedAdd.ts
 * 작성자 : 강동형
 * 작성일 : 2025-03-01
 * ================================================================ 
 * 수정 : 2025-07-18, 강동형 : 취소 버튼 추가
 * 수정 : 2026-02-05, 강동형 : 학과 등록 기능 추가
 * ================================================================ */

import React, { useEffect, useState } from 'react'
import { styled, Box, Typography, TextField, IconButton } from '@mui/material'; 
import { PromoResultType } from 'src/pages/PromoResultPage';
import { promoResultType } from 'src/types/PromoType';
import InputBoxOnChange from '../inputbox/InputBoxOnChange';
import DatePickerInterestedAddRecentDate from './DatePickerInterestedAddRecentDate';
import { usePromoAddInterest } from 'src/stores/PromoStore';
import DatePickerInterestedAddRecentDateBirthDate from './DatePickerInterestedAddRecentDateBirthDate';
import ButtonResultAddInterest from './ButtonResultAddInterest';
import PromoInterestedSelectType from './PromoInterestedSelectType';
import { AlertSpan, NextButton, NextButtonWrapper } from '../common/CommonStyled';
import { Icon_ArrowBack } from '../common/CommonIcons';

interface Props{
    setTab : React.Dispatch<React.SetStateAction<PromoResultType>>;
    currentPromotion: promoResultType | undefined
}

const Container = styled(Box)`
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  height: calc(100vh - 150px);
  overflow-y: auto;
`;

const Label = styled(Typography)({
  color: '#7F7F89',
  fontSize: '14px',
  paddingBottom: '10px'
})

const LargeTextField = styled(TextField)`
  height: '100%';
`;

const Header = styled(Box)({
  padding:'20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center', // ✅ camelCase로 수정
  // paddingBottom: '32px',
  fontSize:'18px',
  fontWeight:'bold'
});

const GenderWrapper = styled('div')({
  display : 'flex',
  alignItems: 'baseline',
  justifyContent :'center',
  border : '1px solid #ccc',
  padding: '3px',
  borderRadius:'5px',
  minHeight:'46px',
  maxHeight:'46px'
})

const GenderContainer = styled('div')<{ isSelected: boolean }>(({ isSelected }) => ({
  display : 'flex',
  alignItems: 'center',
  justifyContent :'center',
  height:'100%',
  width:'100%',
  borderRadius:'4px',
  fontSize:'14px',
  backgroundColor: isSelected ? '#06799B' : 'none',
  color : isSelected ? 'white' : 'black'
}))

const PromoInterestedAdd = ({setTab, currentPromotion}:Props) => {
    const school_class = currentPromotion?.school?.education_class === '고등학교' ? 'high' : currentPromotion?.school?.education_class === '대학교' ? 'univ':'etc'
    const schoolData = currentPromotion?.school
    const setField = usePromoAddInterest((state)=>state.setField)
    const [gender,setGender] = useState(true)
    const [name, setName] = useState('')
    const [validName, setValidName] = useState(true)
    const [phone, setPhone] = useState('')
    const [validPhone, setValidPhone] = useState(true)
    const [major, setMajor] = useState('')

    useEffect(() => {
      if (!schoolData) return;
      if (school_class === 'high') {
        setField('school_class', 'high')
      } else if (school_class === 'univ'){
        setField('school_class', 'univ')
      } else { 
        setField('school_class','etc')
      }
    }, [schoolData, school_class]);
   
    useEffect(()=>{
      setField('promotion_id',currentPromotion?.result_id)
      setField('school_id', currentPromotion?.school_id); // id 넣기
    },[])

    useEffect(()=>{
      if(gender){
        setField('gender','남자')
      }
      else{
        setField('gender','여자')
      }
    },[gender])

    const handleName = (e:React.ChangeEvent<HTMLInputElement>)=>{
      setName(e.target.value)
      setField('name',e.target.value)
    }

    useEffect(() => {
      const regex = /^[가-힣]{2,5}$/
      if (name === '') {
        setValidName(true); // 초기값이나 빈 문자열은 유효한 상태로 처리할 수도 있음
      } else {
        setValidName(regex.test(name));
      }
    }, [name]);

    const handlePhone = (e:React.ChangeEvent<HTMLInputElement>)=>{
      setPhone(e.target.value)
      setField('phone',e.target.value)
    }

    const handleMajor = (e:React.ChangeEvent<HTMLInputElement>)=>{
      setMajor(e.target.value)
      setField('major',e.target.value)
    }

    useEffect(() => {
      const regex = /^\d{10,11}$/
      if (phone === '') {
        setValidPhone(true); // 초기값이나 빈 문자열은 유효한 상태로 처리할 수도 있음
      } else {
        setValidPhone(regex.test(phone));
      }
    }, [phone]);

  return (
    <>
      <Header>
        <IconButton onClick={() => setTab('detail')} sx={{position:'absolute', left:'10px'}}>
          <Icon_ArrowBack/>
        </IconButton>
        관심 표명자 추가
      </Header>
      <Container>
        <Label>*학교명</Label>
          <Box display="flex" alignItems="baseline" justifyContent="flex-end">
          <InputBoxOnChange 
            value={
              schoolData
                ? 
                  schoolData.school_name
                  : ''
            }
            disabled={true}
          />
          </Box>
        <Label marginTop='10px'>*최근상담일자</Label>
          <DatePickerInterestedAddRecentDate/>
        <Label marginTop='10px'>*이름</Label>
          <Box display="flex" alignItems="baseline" justifyContent="flex-end" >
              <InputBoxOnChange onChange={handleName} value={name}/>
          </Box>
          {!validName && <AlertSpan $isValid={false}>한글만 사용한 2~5자여야합니다.</AlertSpan>}
        <Label marginTop='10px'>*연락처</Label>
          <Box display="flex" alignItems="baseline" justifyContent="flex-end">
              <InputBoxOnChange onChange={handlePhone} value={phone}/>
          </Box>
          {!validPhone && <AlertSpan $isValid={false}>숫자만 사용한 10~11자여야합니다.</AlertSpan>}
        <Label marginTop='10px'>*생년월일</Label>
          <DatePickerInterestedAddRecentDateBirthDate/>
        <Label marginTop='10px'>*학과</Label>
          <Box display="flex" alignItems="baseline" justifyContent="flex-end">
              <InputBoxOnChange onChange={handleMajor} value={major}/>
          </Box>
        <Label marginTop='10px'>*성별</Label>
          <GenderWrapper >
              <GenderContainer isSelected={gender} onClick={()=>setGender(true)}>
                남자
              </GenderContainer>
              <GenderContainer isSelected={!gender} onClick={()=>setGender(false)}>
                여자
              </GenderContainer>
          </GenderWrapper>
        <Label marginTop='10px'>*상태</Label>
        <Box display="flex" alignItems="baseline" justifyContent="flex-end">
            <PromoInterestedSelectType/>
        </Box>
        <Box flex={1} display='flex'flexDirection={'column'}>
        <Label marginTop='10px'>메모</Label>
        <LargeTextField fullWidth onChange={(e)=>setField('memo',e.target.value)} multiline rows={4} placeholder="메모를 입력하세요" />
        </Box>
      </Container>
      <NextButtonWrapper $isBorder={true} style={{gap:'10px'}}>
        <NextButton style={{background:'#F5F5F5', color:'#7F7F89', minWidth:'90px', width:'90px', boxShadow:'none'}} onClick={()=>setTab('detail')}>취소</NextButton>
        <ButtonResultAddInterest setTab={setTab} isValid={name !== '' && phone !== '' && validName && validPhone}/>
      </NextButtonWrapper>
    </>
  )
}

export default PromoInterestedAdd